/**
 * PhoneStatusLights — Header bar indicator showing which SIP users
 * are currently on the phone.
 *
 * Green dot = available, Red dot = on a call.
 * Listens to Socket.IO 'yay:phone-status' for real-time updates,
 * with an initial fetch from GET /api/yay/phone-status.
 *
 * Props:
 *   - socket: Socket.IO client instance
 *   - isDark: boolean
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const STYLES = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  user: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  dot: (onCall) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: onCall ? '#ef4444' : '#22c55e',
    boxShadow: onCall
      ? '0 0 6px rgba(239, 68, 68, 0.7)'
      : '0 0 6px rgba(34, 197, 94, 0.5)',
    flexShrink: 0,
  }),
  name: (isDark) => ({
    color: isDark ? '#e2e8f0' : '#f1f6ff',
    opacity: 0.9,
  }),
};

export default function PhoneStatusLights({ socket, isDark = false }) {
  const [users, setUsers] = useState([]);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/yay/phone-status')
      .then(res => {
        if (!cancelled && Array.isArray(res.data?.users)) {
          setUsers(res.data.users);
        }
      })
      .catch(() => { /* ignore — will update via socket */ });
    return () => { cancelled = true; };
  }, []);

  // Socket.IO real-time updates
  const handlePhoneStatus = useCallback((data) => {
    if (Array.isArray(data?.users)) {
      setUsers(data.users);
    }
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('yay:phone-status', handlePhoneStatus);
    return () => {
      socket.off('yay:phone-status', handlePhoneStatus);
    };
  }, [socket, handlePhoneStatus]);

  if (users.length === 0) return null;

  return (
    <div style={STYLES.container} title="Phone status">
      {users.map(u => (
        <span
          key={u.UserKey}
          style={STYLES.user}
          title={u.onCall ? `${u.ShortName} — on a call` : `${u.ShortName} — available`}
        >
          <span style={STYLES.dot(u.onCall)} />
          <span style={STYLES.name(isDark)}>{u.ShortName}</span>
        </span>
      ))}
    </div>
  );
}
